import { Radio, ScrollArea, Table } from '@mantine/core';
import { useTranslation } from 'react-i18next';

export interface InstanceOption {
    sku: string;
    vcpu: number | string;
    memory: string;
    ssd: string;
    bandwidth: string;
    priceHr: string;
    priceMo: string;
}

interface InstanceFamilyOptionListProps {
    options: InstanceOption[];
    selectedSku?: string;
    onSelectInstance: (sku: string) => void;
    isLoading?: boolean;
    priceSuffix?: { primary: string; secondary: string };
}

const tableStyles = {
    table: {
        minWidth: '100%',
    },
    thead: {
        backgroundColor: 'var(--surface-1)',
        borderBottom: '0.5px solid var(--line)',
    },
    th: {
        position: 'sticky' as const,
        top: 0,
        zIndex: 1,
        backgroundColor: 'var(--surface-1)',
        fontWeight: 600,
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: 'color-mix(in srgb, var(--fg) 68%, transparent)',
        padding: '0.75rem 1rem',
    },
    td: {
        padding: '0.75rem 1rem',
        color: 'var(--color-text-primary)',
        backgroundColor: 'var(--surface-2)',
    },
    tr: {
        transition: 'background-color 0.15s ease',
        borderBottom: '0.5px solid var(--line)',
    },
};

export default function InstanceFamilyOptionList({
    options,
    selectedSku,
    onSelectInstance,
    isLoading = false,
    priceSuffix,
}: InstanceFamilyOptionListProps) {
    const { t } = useTranslation('components');
    const resolvedPriceSuffix = priceSuffix ?? {
        primary: t('instanceFamilyOptionList.priceHourly'),
        secondary: t('instanceFamilyOptionList.priceMonthly'),
    };

    if (isLoading) {
        return (
            <div className="text-center text-fg/68">{t('instanceFamilyOptionList.loading')}</div>
        );
    }

    if (!options || options.length === 0) {
        return (
            <div className="text-center text-fg/68">
                {t('instanceFamilyOptionList.noInstances')}
            </div>
        );
    }

    return (
        <div className="rounded-md border border-line overflow-hidden">
            <ScrollArea h={420} offsetScrollbars scrollbarSize={8}>
                <Table highlightOnHover styles={tableStyles}>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>{t('instanceFamilyOptionList.headers.sku')}</Table.Th>
                            <Table.Th>{t('instanceFamilyOptionList.headers.vcpu')}</Table.Th>
                            <Table.Th>{t('instanceFamilyOptionList.headers.memory')}</Table.Th>
                            <Table.Th>{t('instanceFamilyOptionList.headers.ssd')}</Table.Th>
                            <Table.Th>{t('instanceFamilyOptionList.headers.bandwidth')}</Table.Th>
                            <Table.Th style={{ textAlign: 'right' }}>
                                {t('instanceFamilyOptionList.headers.price')}
                            </Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {options.map((instance) => (
                            <Table.Tr
                                key={instance.sku}
                                onClick={() => onSelectInstance(instance.sku)}
                                style={{
                                    cursor: 'pointer',
                                    backgroundColor:
                                        selectedSku === instance.sku
                                            ? 'var(--color-selected-bg)'
                                            : undefined,
                                }}
                            >
                                <Table.Td
                                    style={{
                                        backgroundColor:
                                            selectedSku === instance.sku
                                                ? 'var(--color-selected-bg)'
                                                : 'var(--surface-2)',
                                    }}
                                >
                                    <div className="flex items-center gap-3">
                                        <Radio
                                            size="xs"
                                            aria-label={instance.sku}
                                            checked={selectedSku === instance.sku}
                                            onChange={() => onSelectInstance(instance.sku)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                        <span className="text-sm font-bold text-nowrap">
                                            {instance.sku}
                                        </span>
                                    </div>
                                </Table.Td>
                                <Table.Td
                                    className="text-fg"
                                    style={{
                                        backgroundColor:
                                            selectedSku === instance.sku
                                                ? 'var(--color-selected-bg)'
                                                : 'var(--surface-2)',
                                    }}
                                >
                                    {instance.vcpu}
                                </Table.Td>
                                <Table.Td
                                    className="text-fg"
                                    style={{
                                        backgroundColor:
                                            selectedSku === instance.sku
                                                ? 'var(--color-selected-bg)'
                                                : 'var(--surface-2)',
                                    }}
                                >
                                    {instance.memory}
                                </Table.Td>
                                <Table.Td
                                    className="text-fg"
                                    style={{
                                        backgroundColor:
                                            selectedSku === instance.sku
                                                ? 'var(--color-selected-bg)'
                                                : 'var(--surface-2)',
                                        textWrap: 'nowrap',
                                    }}
                                >
                                    {instance.ssd}
                                </Table.Td>
                                <Table.Td
                                    className="text-fg"
                                    style={{
                                        backgroundColor:
                                            selectedSku === instance.sku
                                                ? 'var(--color-selected-bg)'
                                                : 'var(--surface-2)',
                                    }}
                                >
                                    {instance.bandwidth}
                                </Table.Td>
                                <Table.Td
                                    style={{
                                        textAlign: 'right',
                                        backgroundColor:
                                            selectedSku === instance.sku
                                                ? 'var(--color-selected-bg)'
                                                : 'var(--surface-2)',
                                    }}
                                >
                                    <div className="flex flex-col items-end text-nowrap gap-1">
                                        <div className="text-fg text-sm flex items-center font-bold">
                                            &nbsp;
                                            <span>
                                                {instance.priceHr}
                                                {resolvedPriceSuffix.primary}
                                            </span>
                                        </div>
                                        <div className="text-fg/68 text-sm flex items-center">
                                            &nbsp;
                                            <span>
                                                {instance.priceMo}
                                                {resolvedPriceSuffix.secondary}
                                            </span>
                                        </div>
                                    </div>
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </ScrollArea>
        </div>
    );
}
